import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Check, Loader2, GraduationCap, UserCheck, Clock, Gem } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALENDLY_COACH_URL = process.env.REACT_APP_CALENDLY_COACH_URL || "https://calendly.com/fgallibusiness/30min";

const budgetOptions = [
  { value: "under_1500", label: "Meno di 1.500 €", hint: "Non pronto a investire ora" },
  { value: "1500_3000", label: "1.500 € – 3.000 €", hint: "" },
  { value: "3000_6000", label: "3.000 € – 6.000 €", hint: "" },
  { value: "over_6000", label: "Oltre 6.000 €", hint: "" },
];

const perks = [
  { icon: UserCheck, title: "Solo 1to1", body: "Mai in gruppo. Un percorso cucito interamente su di te." },
  { icon: GraduationCap, title: "Certificazione", body: "Un metodo completo per diventare coach, con attestato finale." },
  { icon: Clock, title: "Call gratuita 30'", body: "Una conoscitiva senza impegno per capire se fa per te." },
  { icon: Gem, title: "Solo 10 posti/anno", body: "Accesso estremamente selettivo. Pochi, seguiti davvero." },
];

const initial = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  background: "",
  motivation: "",
  budget: "",
  consent: false,
  website: "",
};

export default function DiventaCoach() {
  const [data, setData] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const valid =
    data.first_name.trim() &&
    data.last_name.trim() &&
    /\S+@\S+\.\S+/.test(data.email) &&
    data.phone.trim() &&
    data.background.trim().length > 10 &&
    data.motivation.trim().length > 10 &&
    data.budget &&
    data.consent;

  const submit = async () => {
    if (!valid) {
      toast.error("Completa tutti i campi e accetta la privacy per candidarti.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/coach-applications`, data);
      setResult(res.data);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Qualcosa è andato storto. Riprova tra poco.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 py-3 text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none transition-colors duration-300";

  return (
    <section
      id="formazione"
      className="relative bg-background py-28 md:py-40 border-t border-white/10"
      data-testid="diventa-coach-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 md:gap-16">
          {/* Left: narrative */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-4 mb-6">
              <span className="h-px w-10 bg-primary" />
              <span className="text-[11px] tracking-[0.35em] text-primary">
                DIVENTA COACH
              </span>
            </div>
            <h2 className="font-serif font-light text-4xl md:text-5xl text-foreground leading-tight">
              Trasforma la tua vocazione in professione.
            </h2>
            <p className="mt-6 text-muted-foreground leading-relaxed max-w-md">
              Un percorso di formazione individuale per chi vuole diventare
              mental coach con un metodo solido. Niente aule affollate: solo tu,
              io e un cammino di crescita reale. Apro solo 10 posti ogni anno.
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {perks.map((p, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="rounded-xl border border-white/10 bg-card p-5"
                  data-testid={`coach-perk-${i}`}
                >
                  <p.icon className="text-primary mb-3" size={22} strokeWidth={1.3} />
                  <h3 className="text-foreground text-sm font-medium mb-1">{p.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{p.body}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <div className="rounded-2xl border border-white/10 bg-card p-6 md:p-10">
              <AnimatePresence mode="wait">
                {result ? (
                  <motion.div
                    key="res"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center py-6"
                    data-testid={result.qualified ? "coach-result-qualified" : "coach-result-unqualified"}
                  >
                    <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-primary/15 text-primary">
                      <Check size={26} />
                    </div>
                    {result.qualified ? (
                      <>
                        <h3 className="font-serif text-2xl md:text-3xl text-foreground mb-3">
                          Candidatura ricevuta, {data.first_name}.
                        </h3>
                        <p className="text-muted-foreground max-w-md mx-auto leading-relaxed mb-8">
                          Il tuo profilo rientra tra quelli che valuto per i 10
                          posti annuali. Prenota la tua call conoscitiva gratuita
                          di 30 minuti.
                        </p>
                        {CALENDLY_COACH_URL ? (
                          <div className="rounded-xl overflow-hidden border border-white/10">
                            <iframe
                              title="Prenota call coach"
                              src={CALENDLY_COACH_URL}
                              className="w-full"
                              style={{ height: 640, background: "hsl(0 0% 6%)" }}
                              data-testid="coach-calendly-embed"
                            />
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <>
                        <h3 className="font-serif text-2xl md:text-3xl text-foreground mb-3">
                          Grazie per la tua onestà, {data.first_name}.
                        </h3>
                        <p className="text-muted-foreground max-w-md mx-auto leading-relaxed">
                          Un percorso di formazione serio richiede un investimento
                          importante. In questo momento potrebbe non essere il
                          passo giusto per te — quando lo sarà, sarò qui ad
                          aspettarti.
                        </p>
                      </>
                    )}
                  </motion.div>
                ) : (
                  <motion.div key="form" initial={{ opacity: 1 }}>
                    <h3 className="font-serif text-2xl md:text-3xl text-foreground mb-8">
                      Candidati al percorso.
                    </h3>
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      value={data.website}
                      onChange={(e) => update("website", e.target.value)}
                      style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                      data-testid="coach-hp-website"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <input className={inputCls} placeholder="Nome" value={data.first_name} onChange={(e) => update("first_name", e.target.value)} data-testid="coach-first-name" />
                      <input className={inputCls} placeholder="Cognome" value={data.last_name} onChange={(e) => update("last_name", e.target.value)} data-testid="coach-last-name" />
                      <input className={inputCls} type="email" placeholder="Email" value={data.email} onChange={(e) => update("email", e.target.value)} data-testid="coach-email" />
                      <input className={inputCls} placeholder="Telefono" value={data.phone} onChange={(e) => update("phone", e.target.value)} data-testid="coach-phone" />
                    </div>

                    <div className="mb-6">
                      <label className="text-sm text-muted-foreground">La tua esperienza attuale (coaching, PNL, ambiti affini)</label>
                      <textarea className={`${inputCls} resize-none mt-2`} rows={2} placeholder="Raccontami da dove parti..." value={data.background} onChange={(e) => update("background", e.target.value)} data-testid="coach-background" />
                    </div>
                    <div className="mb-8">
                      <label className="text-sm text-muted-foreground">Perché vuoi diventare coach?</label>
                      <textarea className={`${inputCls} resize-none mt-2`} rows={2} placeholder="La tua motivazione..." value={data.motivation} onChange={(e) => update("motivation", e.target.value)} data-testid="coach-motivation" />
                    </div>

                    <div className="text-sm text-muted-foreground mb-4">Quanto sei pronto a investire nella tua formazione?</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                      {budgetOptions.map((o) => (
                        <button
                          key={o.value}
                          type="button"
                          onClick={() => update("budget", o.value)}
                          className={`text-left rounded-xl border p-4 transition-colors duration-300 ${
                            data.budget === o.value ? "border-primary bg-primary/5" : "border-white/10 hover:border-white/30"
                          }`}
                          data-testid={`coach-budget-${o.value}`}
                        >
                          <div className="text-foreground text-sm font-medium">{o.label}</div>
                          {o.hint && <div className="text-xs text-muted-foreground mt-1">{o.hint}</div>}
                        </button>
                      ))}
                    </div>

                    <div className="mb-8 flex items-start gap-3">
                      <button
                        type="button"
                        onClick={() => update("consent", !data.consent)}
                        aria-pressed={data.consent}
                        data-testid="coach-consent"
                        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors duration-300 ${data.consent ? "border-primary bg-primary text-primary-foreground" : "border-white/30 hover:border-white/60"}`}
                      >
                        {data.consent && <Check size={14} strokeWidth={3} />}
                      </button>
                      <span className="text-xs text-muted-foreground leading-relaxed max-w-xl">
                        <span onClick={() => update("consent", !data.consent)} className="cursor-pointer">Accetto l'{" "}</span>
                        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 hover:text-primary/80">informativa sulla privacy</a>{" "}
                        <span onClick={() => update("consent", !data.consent)} className="cursor-pointer">e il trattamento dei dati (GDPR).</span>
                      </span>
                    </div>

                    <button
                      onClick={submit}
                      disabled={submitting}
                      className="inline-flex items-center gap-3 rounded-full bg-primary text-primary-foreground px-8 py-3.5 text-sm tracking-[0.12em] hover:bg-primary/90 transition-colors duration-300 disabled:opacity-60"
                      data-testid="coach-submit"
                    >
                      {submitting ? (<><Loader2 size={16} className="animate-spin" /> INVIO...</>) : "CANDIDATI AL PERCORSO →"}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
